import React, { useCallback, useEffect, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button, CircularProgress, IconButton, MenuItem, Stack, Typography } from "@mui/material";
import { ProgressOverlay, RoleComponentGuard } from "components";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { AiLoadingState } from "components/modals/Deal-create/components/AiLoadingState";
import { useActionHook, useModals, useNavigate } from "hooks";
import { useDealActions, usePickUserPermissions, useUserRoleSelector } from "store";
import { UserPermissionType } from "store/types";
import pickFiles from "utils/pick-files";

import { RoleName } from "../../../constants";
import { defaultState, FormContext, State } from "../Deal-form-drawer";
import { CategoriesTabs } from "../Deal-form-drawer/CategoriesTabs";
import { Category } from "../Deal-form-drawer/Category";
import { ContactList } from "../Deal-form-drawer/ContactList";
import { CreateChar } from "../Deal-form-drawer/CreateChar";
import { useCharDataHooks } from "../Deal-form-drawer/hooks/use-char-data-hooks";
import { useCharHooks } from "../Deal-form-drawer/hooks/use-char-hooks";
import { SelectField } from "../Deal-form-drawer/SelectField";

const ACCEPTED_TYPES = ".pdf";

const EXTRACT_MESSAGES = [
  "Reading your document...",
  "Extracting characteristics...",
  "Mapping to the form...",
  "Almost there..."
];

// Server validation errors come back keyed like "characteristic_data.{index}.{field}". We resolve the index
// against the submitted array and re-key by (characteristic + investor + round) so the field can find its own error.
type CharDataErrors = Record<string, Record<string, string>>;

const charDataErrorKey = (params: {
  characteristic_id: number | string;
  investor_id: number | null;
  round_id: number | null;
}) => `${params.characteristic_id}|${params.investor_id ?? ""}|${params.round_id ?? ""}`;

interface Props {
  dealId: number;
  dealName: string;
  /** Injected by the prompt system. */
  onCancel: () => void;
  onSubmit: (result: boolean) => void;
}

export const DealCharacteristicsModal: React.FC<Props> = ({ dealId, dealName, onCancel, onSubmit }) => {
  const {
    getDealCharacteristicsDataAction,
    postDealCharacteristicsDataAction,
    getAssetClassesAction,
    getDealAndStoreAction,
    extractDealFromFileAction
  } = useDealActions();
  const { errorModal } = useModals();
  const navigate = useNavigate();

  const featurePermission = usePickUserPermissions("characteristics");
  const userRole = useUserRoleSelector();
  const isAdminRole = userRole?.name === RoleName.Admin;
  const featureViewOnly = featurePermission === UserPermissionType.VIEWER;
  const featureAdmin = featurePermission === UserPermissionType.ADMIN;
  const viewOnly = featureViewOnly;

  const [state, setState] = useState<State>(defaultState);
  const [dataErrors, setDataErrors] = useState<CharDataErrors>({});

  const {
    data: assetClasses,
    loading: assetClassesLoading,
    action: getAssetClasses
  } = useActionHook(getAssetClassesAction);

  const subAssetClasses = useMemo(
    () => assetClasses?.find((item) => item.id === state.assetClassId)?.sub_asset_class || [],
    [assetClasses, state.assetClassId]
  );

  const { createChar, updateChar, deleteChar, getCharacteristics } = useCharHooks({ state, setState });
  const {
    updateCharData,
    buildNewCharData,
    handleManyCharData,
    getOneData,
    getManyData,
    overrideCharacteristicData,
    isOverrideAvailable
  } = useCharDataHooks({ state, setState });

  const { action: getData, loading } = useActionHook(getDealCharacteristicsDataAction, {
    onSuccess: (res) => {
      const subBelongsToAsset =
        res.sub_asset_class?.id != null &&
        res.asset_class?.id != null &&
        res.sub_asset_class?.parent_id === res.asset_class?.id;

      setState({
        category: "General",
        categories: res.categories,
        subCategories: res.subcategories,
        characteristics: res.characteristics.map((item) => ({
          ...item,
          is_deleted: false,
          is_locked_to_edit: Boolean(item.is_locked_to_edit ?? false),
          category_name: item.category_name
        })),
        data: res.characteristic_data.map((item) => ({ ...item, is_deleted: false })),
        contact: res.contact ? res.contact : [],
        assetClassId: res.asset_class.id,
        subAssetClassId: subBelongsToAsset ? res.sub_asset_class!.id : null,
        showAssetClass: res.show_asset_class,
        characteristic_subcategory_id: res.characteristic_subcategory_id,
        showSubAssetClass: res.show_sub_asset_class,
        isDebt: res.type === "debt",
        hasSubDeals: res.has_sub_deals
      });
    }
  });

  const { action: postData, loading: postLoading } = useActionHook(postDealCharacteristicsDataAction, {
    onSuccess: () => {
      getDealAndStoreAction(dealId).catch(() => undefined);
    }
  });

  const { action: extractFromFile, loading: extractLoading } = useActionHook(extractDealFromFileAction, {
    onReject: () => errorModal({ msg: "Could not read the file" })
  });

  useEffect(() => {
    if (!dealId) return;
    getData(dealId);
    getAssetClasses();
  }, [dealId]);

  const handleChangeTab = useCallback((value: string) => {
    setState((prev) => ({ ...prev, category: value }));
  }, []);

  const handleShowAssets = ({
    field,
    value
  }: {
    field: keyof Pick<State, "assetClassId" | "subAssetClassId" | "showAssetClass" | "showSubAssetClass">;
    value: boolean | number | null;
  }) => {
    setState((prev) => ({ ...prev, [field]: value, ...(field === "assetClassId" && { subAssetClassId: null }) }));
  };

  const openDeal = () => {
    navigate({ route: "deal", params: { deal_id: String(dealId) } });
    onCancel();
  };

  // Merge extracted values but NEVER overwrite an already-filled characteristic — only fill empty ones.
  const applyExtraction = async (file: File) => {
    const res = await extractFromFile(file);
    setState((prev) => {
      const data = [...prev.data];
      let maxId = data.reduce((acc, item) => Math.max(acc, Number(item.id) || 0), 0);
      const dealDataId = data[0]?.deal_id || dealId;

      const assetPatch: Partial<State> = {};
      if (res.asset_class_id != null && prev.assetClassId == null) {
        assetPatch.assetClassId = res.asset_class_id;
        assetPatch.subAssetClassId = res.sub_asset_class_id ?? null;
      } else if (res.sub_asset_class_id != null && prev.subAssetClassId == null) {
        assetPatch.subAssetClassId = res.sub_asset_class_id;
      }

      res.characteristics?.forEach(({ characteristic_id, value }) => {
        if (value == null || String(value).trim() === "") return;
        const idx = data.findIndex(
          (d) => d.characteristic_id === characteristic_id && d.investor_id == null && d.round_id == null && !d.is_deleted
        );
        if (idx >= 0) {
          if (data[idx].value?.trim()) return; // already filled — keep existing
          data[idx] = { ...data[idx], value: String(value) };
        } else {
          maxId += 1;
          data.push({
            id: String(maxId),
            deal_id: dealDataId,
            characteristic_id,
            is_default: false,
            percentage: 100,
            value: String(value),
            currency: null,
            is_deleted: false,
            investor_id: null,
            round_id: null
          });
        }
      });

      return { ...prev, ...assetPatch, data };
    });
  };

  const handleBrowse = async () => {
    const fileList = await pickFiles({ accept: ACCEPTED_TYPES });
    if (fileList?.length) applyExtraction(fileList[0]);
  };

  const handleSubmit = async () => {
    if (!state.assetClassId) return;
    setDataErrors({});
    const validSubId =
      state.subAssetClassId != null && subAssetClasses.some((s) => s.id === state.subAssetClassId)
        ? state.subAssetClassId
        : null;

    const CURRENCY_CHARACTERISTIC_NAME = "Currency";
    const characteristic_data = state.data.map((item) => {
      const char = state.characteristics.find((c) => c.id === item.characteristic_id);
      if (
        char?.name === CURRENCY_CHARACTERISTIC_NAME &&
        char?.data_type === "currency" &&
        (item.currency === null || item.currency === "") &&
        item.value
      ) {
        return { ...item, currency: item.value };
      }
      return item;
    });

    try {
      await postData({
        deal_id: dealId,
        show_asset_class: state.showAssetClass,
        show_sub_asset_class: state.showSubAssetClass,
        asset_class_id: state.assetClassId,
        sub_asset_class_id: validSubId,
        type: state.isDebt ? "debt" : "",
        has_sub_deals: state.hasSubDeals,
        characteristics: state.characteristics,
        characteristic_data,
        contact: (state.contact || []).map((contact) =>
          typeof contact.id === "string" ? { ...contact, id: undefined } : contact
        ),
        characteristic_subcategory_id: state.characteristic_subcategory_id
      });
      onSubmit(true);
    } catch (error) {
      const apiErrors = (error as { errors?: Record<string, string[]> })?.errors;
      const nextErrors: CharDataErrors = {};

      if (apiErrors) {
        Object.entries(apiErrors).forEach(([key, messages]) => {
          const match = key.match(/^characteristic_data\.(\d+)\.(\w+)$/);
          if (!match) return;
          const item = characteristic_data[Number(match[1])];
          if (!item) return;
          const composite = charDataErrorKey(item);
          nextErrors[composite] = { ...nextErrors[composite], [match[2]]: messages.join(" ") };
        });
      }

      if (Object.keys(nextErrors).length) {
        setDataErrors(nextErrors);
      } else {
        errorModal({ msg: "Something went wrong" });
      }
    }
  };

  const getCharDataError = useCallback(
    (params: { characteristic_id: number | string; investor_id: number | null; round_id: number | null }) =>
      dataErrors[charDataErrorKey(params)],
    [dataErrors]
  );

  const clearCharDataError = useCallback(
    (
      params: { characteristic_id: number | string; investor_id: number | null; round_id: number | null },
      field?: string
    ) => {
      const key = charDataErrorKey(params);
      setDataErrors((prev) => {
        if (!prev[key]) return prev;
        const { [key]: rowErrors, ...rest } = prev;
        if (!field) return rest;
        const { [field]: _removed, ...restFields } = rowErrors;
        return Object.keys(restFields).length ? { ...rest, [key]: restFields } : rest;
      });
    },
    []
  );

  const formContextValue = useMemo(
    () => ({
      getCharacteristics,
      getOneData,
      getManyData,
      createChar,
      updateChar,
      deleteChar,
      updateCharData,
      buildNewCharData,
      handleManyCharData,
      setCharacteristicRef: () => undefined,
      overrideCharacteristicData,
      isOverrideAvailable,
      getCharDataError,
      clearCharDataError,
      viewBy: null,
      dealId
    }),
    [
      getCharacteristics,
      getOneData,
      getManyData,
      createChar,
      updateChar,
      deleteChar,
      updateCharData,
      buildNewCharData,
      handleManyCharData,
      overrideCharacteristicData,
      isOverrideAvailable,
      getCharDataError,
      clearCharDataError,
      dealId
    ]
  );

  const canEdit = !viewOnly && (featureAdmin || isAdminRole);

  return (
    <Box
      sx={{ width: "760px", maxWidth: "92vw", maxHeight: "85vh", display: "flex", flexDirection: "column", p: "32px" }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: "16px" }}>
        <Typography sx={{ fontSize: "18px", fontWeight: 400, color: "#0a3142" }}>Deal characteristics</Typography>
        <IconButton onClick={() => onCancel()} sx={{ p: 0 }}>
          <CloseIcon sx={{ color: "#0a3142" }} />
        </IconButton>
      </Stack>
      <Box sx={{ height: "1px", bgcolor: "#DBE8F5", mb: "20px" }} />

      {loading || assetClassesLoading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: "160px" }}>
          <CircularProgress size={40} sx={{ color: "#0a3142" }} />
        </Stack>
      ) : extractLoading ? (
        <Box sx={{ minHeight: "320px", display: "flex" }}>
          <AiLoadingState isLoading messages={EXTRACT_MESSAGES} />
        </Box>
      ) : (
        <>
          <FormContext.Provider value={formContextValue}>
          <ProgressOverlay
            inProgress={postLoading}
            containerSX={{ display: "flex", flexDirection: "column", gap: "16px", overflow: "hidden" }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap="16px">
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 500,
                  color: "#0a3142",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                {dealName}
              </Typography>
              <Button
                onClick={openDeal}
                startIcon={<LaunchOutlinedIcon sx={{ fontSize: "18px" }} />}
                sx={{
                  textTransform: "none",
                  color: "#12a594",
                  fontSize: "14px",
                  whiteSpace: "nowrap",
                  minWidth: "auto"
                }}
              >
                Open Deal
              </Button>
            </Stack>

            <CategoriesTabs
              categories={state.categories.filter((category) => category !== "Asset Report")}
              value={state.category}
              onChange={handleChangeTab}
            />

            <Stack sx={{ overflow: "auto", gap: "16px", pt: "8px", pr: "4px" }}>
              {state.category === "Contact" ? (
                <ContactList state={state} setState={setState} viewOnly={!canEdit} />
              ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: "20px", rowGap: "16px" }}>
                  {state.category === "General" && (
                    <>
                      <SelectField
                        label="Asset Class"
                        disabled={!canEdit}
                        value={state.assetClassId}
                        isShow={state.showAssetClass}
                        onChange={(e) => handleShowAssets({ field: "assetClassId", value: Number(e.target.value) })}
                        onIsShow={() => handleShowAssets({ field: "showAssetClass", value: !state.showAssetClass })}
                        SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
                      >
                        {assetClasses?.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.account_alias}
                          </MenuItem>
                        ))}
                      </SelectField>
                      <SelectField
                        {...(state.subAssetClassId && {
                          onClear: () => handleShowAssets({ field: "subAssetClassId", value: null })
                        })}
                        label="Sub Asset Class"
                        disabled={!canEdit}
                        value={state.subAssetClassId}
                        isShow={state.showSubAssetClass}
                        onChange={(e) => handleShowAssets({ field: "subAssetClassId", value: Number(e.target.value) })}
                        onIsShow={() =>
                          handleShowAssets({ field: "showSubAssetClass", value: !state.showSubAssetClass })
                        }
                        SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
                      >
                        {subAssetClasses.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.account_alias}
                          </MenuItem>
                        ))}
                      </SelectField>
                    </>
                  )}
                  <Category categoryName={state.category} viewOnly={!canEdit} />
                </Box>
              )}

              {canEdit && state.category !== "Contact" && (
                <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
                  <Box>
                    <CreateChar />
                  </Box>
                </RoleComponentGuard>
              )}

              {/* Extract characteristics from a PDF file */}
              {canEdit && (
                <Stack direction="row" alignItems="center" gap="6px">
                  <Button
                    variant="text"
                    onClick={handleBrowse}
                    disabled={extractLoading}
                    startIcon={
                      extractLoading ? (
                        <CircularProgress size={14} sx={{ color: "#071F35" }} />
                      ) : (
                        <UploadFileIcon sx={{ fontSize: 16 }} />
                      )
                    }
                    sx={{
                      background: "#f0f0f0",
                      color: "#071F35",
                      fontSize: "12px",
                      textTransform: "none",
                      padding: "4px 10px",
                      "&:hover": { background: "#e6e6e6" }
                    }}
                  >
                    Extract From File
                  </Button>
                  <MaterialTooltip
                    isShow
                    title="Extract characteristics data from your financial documents in PDF format"
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 18, color: "#92a7bc", cursor: "help" }} />
                  </MaterialTooltip>
                </Stack>
              )}
            </Stack>
          </ProgressOverlay>
          </FormContext.Provider>

          <Stack direction="row" justifyContent="flex-end" gap="12px" sx={{ mt: "20px" }}>
            <Button variant="outlined" onClick={() => onCancel()} sx={{ textTransform: "none", borderRadius: "20px" }}>
              Cancel
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                disabled={postLoading}
                onClick={handleSubmit}
                sx={{ textTransform: "none", borderRadius: "20px" }}
              >
                Apply
              </Button>
            )}
          </Stack>
        </>
      )}
    </Box>
  );
};
